import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  DeleteOutlined as DeleteIcon,
  EditOutlined as EditIcon,
} from "@mui/icons-material";
import { DATA_MODEL_GROUPS } from "../../constants/Constants";
import { INDIGO, SLATE } from "../../constants/Colors";

const FIELD_TYPES = [
  { title: "Text", value: "text" },
  { title: "Number", value: "number" },
  { title: "Boolean", value: "boolean" },
  { title: "Array", value: "array" },
  { title: "JSON", value: "json" },
];

const EMPTY_FORM = { name: "", definition: "", field_type: "", group: "" };

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

const FIELD_LABEL_SX = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: SLATE[700],
  mb: "6px",
  display: "block",
};

const COL_TEMPLATE = "180px 180px 1fr 110px 72px";

export default function DataModelingTableSidebar({
  isOpen,
  onClose,
  dataProfileStructures,
  title = "Data Model Definition",
  modelType = "program_profile",
  onSaveRow,
  onDeleteRow,
}) {
  const [editDialog, setEditDialog] = useState({ open: false, row: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, row: null });
  const [deleting, setDeleting] = useState(false);

  const openEdit = (row) => {
    setForm({ name: row.name || "", definition: row.definition || "", field_type: row.field_type || "", group: row.group || "", id: row.id });
    setFormErrors({});
    setEditDialog({ open: true, row });
  };

  const openAdd = () => {
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setEditDialog({ open: true, row: null });
  };

  const closeEdit = () => {
    setEditDialog({ open: false, row: null });
    setForm(EMPTY_FORM);
    setFormErrors({});
  };

  const handleEditSave = async () => {
    const errors = {};
    if (!form.name?.trim()) errors.name = true;
    if (!form.field_type) errors.field_type = true;
    if (!form.group) errors.group = true;
    if (Object.keys(errors).length) { setFormErrors(errors); return; }

    setSaving(true);
    try {
      await onSaveRow({ ...form, model_type: modelType });
      closeEdit();
    } catch {
      // error toasted by useApiCall
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await onDeleteRow(deleteDialog.row);
      setDeleteDialog({ open: false, row: null });
    } catch {
      // error toasted by useApiCall
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        sx={{ zIndex: 1299 }}
        PaperProps={{
          sx: {
            width: "80%",
            minWidth: 640,
            maxWidth: 1200,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            bgcolor: SLATE[50],
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            pt: 2.5,
            pb: 2,
            bgcolor: "#ffffff",
            borderBottom: `1px solid ${SLATE[200]}`,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: SLATE[400], textTransform: "uppercase", letterSpacing: "0.08em", mb: "6px" }}>
              Knowledge Base — Program Data Model
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: SLATE[900] }}>
                {title}
              </Typography>
              {dataProfileStructures?.length > 0 && (
                <Box sx={{ px: "10px", py: "2px", bgcolor: SLATE[100], borderRadius: "20px" }}>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: SLATE[600] }}>
                    {dataProfileStructures.length} fields
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: "2px" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={openAdd}
              sx={{
                borderColor: SLATE[300],
                color: SLATE[700],
                textTransform: "none",
                borderRadius: "8px",
                "&:hover": { borderColor: INDIGO[400], bgcolor: INDIGO[50], color: INDIGO[600] },
              }}
            >
              + Add New
            </Button>
            <IconButton size="small" onClick={onClose} sx={{ color: SLATE[400], "&:hover": { color: SLATE[700], bgcolor: SLATE[100] } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <Box sx={{ bgcolor: "#ffffff", minWidth: 600 }}>
            {/* Column headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: COL_TEMPLATE,
                px: 3,
                py: 1,
                borderBottom: `1px solid ${SLATE[200]}`,
                bgcolor: SLATE[50],
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              {["Group", "Field Name", "Definition", "Type", "Actions"].map((col) => (
                <Typography key={col} sx={{ fontSize: "0.6875rem", fontWeight: 600, color: SLATE[500], textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {col}
                </Typography>
              ))}
            </Box>

            {!dataProfileStructures?.length ? (
              <Box sx={{ textAlign: "center", py: 10 }}>
                <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: SLATE[900], mb: "4px" }}>No fields defined</Typography>
                <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>
                  Click <strong>+ Add New</strong> to define your first field.
                </Typography>
              </Box>
            ) : (
              dataProfileStructures.map((row, idx) => (
                <Box
                  key={row.id || idx}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: COL_TEMPLATE,
                    px: 3,
                    py: "10px",
                    alignItems: "center",
                    borderBottom: idx < dataProfileStructures.length - 1 ? `1px solid ${SLATE[100]}` : "none",
                    "&:hover": { bgcolor: SLATE[50] },
                  }}
                >
                  <Typography sx={{ fontSize: "0.8125rem", color: SLATE[700], fontWeight: 500, pr: 1 }} noWrap>
                    {row.group || "—"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8125rem", color: SLATE[900], fontWeight: 500, pr: 1 }} noWrap>
                    {row.name || "—"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8125rem", color: SLATE[600], pr: 1 }}>
                    {row.definition || "—"}
                  </Typography>
                  <Typography sx={{ fontSize: "0.8125rem", color: SLATE[700] }}>
                    {FIELD_TYPES.find((t) => t.value === row.field_type)?.title || row.field_type || "—"}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="Edit" placement="top">
                      <IconButton size="small" onClick={() => openEdit(row)}
                        sx={{ color: SLATE[400], "&:hover": { color: INDIGO[600], bgcolor: INDIGO[50] } }}>
                        <EditIcon sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" placement="top">
                      <IconButton size="small" onClick={() => setDeleteDialog({ open: true, row })}
                        sx={{ color: SLATE[400], "&:hover": { color: "#ef4444", bgcolor: "#fef2f2" } }}>
                        <DeleteIcon sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Add / Edit dialog */}
      <Dialog open={editDialog.open} onClose={closeEdit} maxWidth="sm" fullWidth sx={{ zIndex: 1400 }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>
            {editDialog.row ? "Edit Field" : "Add Field"}
          </Typography>
          <IconButton size="small" onClick={closeEdit} sx={{ color: SLATE[400] }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important", display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Group <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
            </Typography>
            <Autocomplete
              size="small"
              options={DATA_MODEL_GROUPS}
              value={DATA_MODEL_GROUPS.find((g) => g.value === form.group) || null}
              getOptionLabel={(o) => o.title}
              disableClearable
              componentsProps={{ popper: { sx: { zIndex: 21474836 } } }}
              onChange={(_, val) => { setForm((p) => ({ ...p, group: val?.value || "" })); setFormErrors((p) => ({ ...p, group: false })); }}
              renderInput={(params) => (
                <TextField {...params} error={formErrors.group} helperText={formErrors.group ? "Group is required" : ""} sx={INPUT_SX} />
              )}
            />
          </Box>

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Field Name <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
            </Typography>
            <TextField
              fullWidth size="small"
              placeholder="e.g. program_tuition_fee"
              value={form.name}
              onChange={(e) => { setForm((p) => ({ ...p, name: e.target.value })); setFormErrors((p) => ({ ...p, name: false })); }}
              error={formErrors.name}
              helperText={formErrors.name ? "Field name is required" : ""}
              sx={INPUT_SX}
            />
          </Box>

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Definition</Typography>
            <TextField
              fullWidth size="small" multiline minRows={3}
              placeholder="Describe what this field represents"
              value={form.definition}
              onChange={(e) => setForm((p) => ({ ...p, definition: e.target.value }))}
              sx={INPUT_SX}
            />
          </Box>

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Type <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
            </Typography>
            <TextField
              select fullWidth size="small"
              value={form.field_type}
              onChange={(e) => { setForm((p) => ({ ...p, field_type: e.target.value })); setFormErrors((p) => ({ ...p, field_type: false })); }}
              error={formErrors.field_type}
              helperText={formErrors.field_type ? "Type is required" : ""}
              SelectProps={{ MenuProps: { style: { zIndex: 21474836 } } }}
              sx={INPUT_SX}
            >
              {FIELD_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.title}</MenuItem>)}
            </TextField>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}>
            <Button variant="outlined" size="small" onClick={closeEdit}
              sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}>
              Cancel
            </Button>
            <Button variant="contained" size="small" onClick={handleEditSave} disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ textTransform: "none", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}>
              {saving ? "Saving..." : (editDialog.row ? "Save" : "Add")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, row: null })} maxWidth="xs" fullWidth sx={{ zIndex: 1400 }}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>Confirm Delete</Typography>
          <IconButton size="small" onClick={() => setDeleteDialog({ open: false, row: null })} sx={{ color: SLATE[400] }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography sx={{ fontSize: "0.875rem", color: SLATE[600], mb: 2 }}>
            Are you sure you want to delete <strong>{deleteDialog.row?.name}</strong>? This action cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" size="small" onClick={() => setDeleteDialog({ open: false, row: null })}
              sx={{ borderColor: SLATE[300], color: SLATE[700], textTransform: "none", "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}>
              Cancel
            </Button>
            <Button variant="contained" size="small" color="error" onClick={handleDeleteConfirm} disabled={deleting}
              startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ textTransform: "none", fontWeight: 500, boxShadow: "none", "&:hover": { boxShadow: "none" } }}>
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
